/**
 * recoverAbsenceAdapter — « Planifier un rattrapage » : les absences à rattraper, les paramètres
 * des créneaux et le corps de l'envoi.
 *
 * Port littéral de `student/absence/RecoverFormComponent.js`, sur le chemin parent
 * (`withStudent: true`, donc `asParent`) :
 *  - les options de l'étape « Planifier mes rattrapages » (`attendanceFlagsToBeRecoveredOptions`) :
 *    l'échéance et son compte à rebours sont ceux de `shared/recoveryDeadline`, que la ligne
 *    « À faire » de l'accueil lit aussi ;
 *  - l'étape masquée quand UNE SEULE absence est rattrapable maintenant (`hasOnlyOneRecoverableFlag`) ;
 *  - `getRecoveryParams` → `getRecoveryPossibilitiesAsParent`, `{ student_email, attendance_flag_id }` ;
 *  - `onSubmit` → `recoverAbsenceAsParent` (`POST /api/v1/attendance-flags/{id}/recover-as-parent`),
 *    corps `{ attendance_flag_id?, recovery_occurrence_id, student_email }`.
 *
 * Les créneaux eux-mêmes (« Meilleur groupe », motifs de refus) sont ceux de
 * `RecoverLiveSessionComponent`, déjà portés par `attendanceFlagAdapter`.
 */
import moment from "moment"
import "moment/locale/fr"

import { formatDayMonth, formatDayTime } from "../../../ui/formatters/dateFormatter"
import { type UIParentRecoveryAbsence } from "../../../ui/types"
import { daysLeftLabel, recoveryDeadline, type ApiRecoverableAbsence } from "../shared/recoveryDeadline"

const RECOVERY_UNAVAILABLE_REASON = "Ce rattrapage n'est plus disponible"

/**
 * Les absences à rattraper, dans l'ordre du serveur — celles qui ne se rattrapent plus restent
 * dans la liste, grisées, avec leur raison. `now` est un argument pour que la règle datée se
 * teste temps figé.
 */
export function toUIParentRecoveryAbsences(
    flags: ApiRecoverableAbsence[] | null | undefined,
    now: moment.Moment = moment()
): UIParentRecoveryAbsence[] {
    return (flags ?? [])
        .map((flag): UIParentRecoveryAbsence | null => {
            if (flag?.id == null) return null

            const { expirationDate, daysLeft, urgent } = recoveryDeadline(flag, now)

            const label = flag.date
                ? `Absence du ${formatDayTime(flag.date, false, "sentence")}`
                : "Crédit de rattrapage"

            if (!flag.is_recoverable_now) {
                return { id: flag.id, label, recoverable: false, unavailableReason: RECOVERY_UNAVAILABLE_REASON }
            }

            const deadline = `${flag.date ? "Rattrapage" : "Séance"} à planifier avant le ${formatDayMonth(
                expirationDate.toISOString(),
                false
            )}`

            return {
                id: flag.id,
                label,
                detail: flag.description ? `${flag.description} | ${deadline}` : deadline,
                urgency: urgent ? daysLeftLabel(daysLeft, "label") : undefined,
                recoverable: true,
            }
        })
        .filter((absence): absence is UIParentRecoveryAbsence => absence != null)
}

/**
 * L'absence à rattraper d'office : la seule rattrapable maintenant, ou `null`. Avec elle, l'étape
 * « Quelle absence ? » n'a qu'une réponse, et le legacy la masque (`is_masked`).
 */
export function singleRecoverableAbsence(absences: UIParentRecoveryAbsence[]): UIParentRecoveryAbsence | null {
    const recoverable = absences.filter((absence) => absence.recoverable)
    return recoverable.length === 1 ? (recoverable[0] ?? null) : null
}

/** Paramètres d'URL de `recovery-possibilities`, dans l'ordre de `getRecoveryParams`. */
export function recoverAbsenceSlotsParams(args: { absenceId: string | number; studentEmail?: string | null }): {
    student_email?: string
    attendance_flag_id: string | number
} {
    return {
        ...(args.studentEmail ? { student_email: args.studentEmail } : {}),
        attendance_flag_id: args.absenceId,
    }
}

export type ApiRecoverAbsenceBody = {
    attendance_flag_id?: string | number
    recovery_occurrence_id: string | number
    student_email?: string
}

/**
 * Le corps de `recover-as-parent`. C'est `{ ...data, student_email }` du legacy, où `data` sont
 * les valeurs du formulaire : `attendance_flag_id` n'y figure QUE si l'étape du choix a été
 * montrée — masquée, son champ n'est pas enregistré. Le serveur ne lit que
 * `recovery_occurrence_id` et `student_email` ; l'écart est gardé pour que les deux chemins
 * envoient la même chose.
 */
export function recoverAbsenceBody(args: {
    absenceId: string | number
    slotId: string | number
    studentEmail?: string | null
    /** Vrai quand le parent a choisi l'absence ; faux quand l'étape a été sautée. */
    absencePicked: boolean
}): ApiRecoverAbsenceBody {
    return {
        ...(args.absencePicked ? { attendance_flag_id: args.absenceId } : {}),
        recovery_occurrence_id: args.slotId,
        ...(args.studentEmail ? { student_email: args.studentEmail } : {}),
    }
}
